/**
 * RepairVerdict — did the repair actually hand over the recording?
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * A repair tool built on FFmpeg inherits FFmpeg's contract, and FFmpeg is a
 * transcoder: on a damaged input it writes what it could decode, stops at the
 * first unrecoverable point and exits 0. "Exit 0" therefore means "I wrote
 * something", not "your recording is back". A tool that reads it as success
 * says "Repair Complete" over half a file.
 *
 * Measured on the live /repair-audio page, 2026-09-17, 60 s sources:
 *   mp3 cut in half   → diagnosis "no issues", 1:00 → 0:30, badge "lossless"
 *   ogg with a hole   → diagnosis "no issues", 1:00 → 0:27
 *   flac cut in half  → table says 1:00 → 1:00; the delivered file is 0:30
 *                       (the FLAC header keeps the ORIGINAL sample count, so
 *                       even the after-probe repeats the lie — a delivered
 *                       length is only trustworthy when it came from a decode)
 *
 * The sibling tool learned this three times already (/repair-video's
 * verifyOutput: "0 secs long" 2026-05-09, unplayable mp4 #509 2026-05-24,
 * garbage reconstruction #Camilo) and gained stream/duration/decode gates plus
 * an honest "partial repair" outcome. Audio never got them. This module is
 * that verdict, in one place, so the next repair tool cannot be born without it.
 *
 * ── The two losses are different facts and the user is owed both ────────────
 *   declaredSec   what the file SAYS it is            (header)
 *   presentSec    what is really inside it            (frame census / surgeon)
 *   deliveredSec  what we handed over                 (decode of our output)
 *
 *   delivered < present  → WE dropped audio that was still there → our bug,
 *                          escalate the ladder instead of shipping it.
 *   present  < declared  → the recording was already incomplete when it
 *                          reached us → not our fault, and the one thing the
 *                          user actually wants to know. A transcoder can never
 *                          say it; a repair tool must.
 *
 * Fail-open by design: with no evidence (all zeros) the verdict is 'full' with
 * cause 'unverified', i.e. exactly today's behaviour, never a false alarm.
 */
(function (global) {
  'use strict';

  /* A delivered length within 2 % of what the source holds is the whole file:
   * healthy encodes measured 1.000, and the smallest real loss in the corpus
   * (one 64 KB hole in a 60 s mp3) is 0.955. The absolute floor below keeps a
   * single frame of encoder padding on a 2 s clip from reading as damage. */
  var FULL_BAND = 0.98;
  var MIN_MISSING_SEC = 0.35;
  /* Raw PCM read with the wrong layout comes out LONGER than the source can
   * possibly be — a 54 s payload became 173 s of noise in the corpus (3.2x).
   * Two bands, because the two kinds of evidence are not equally good: a
   * COUNTED source (frame census, RIFF data chunk) may be trusted tightly, a
   * DECLARED one may not — a header routinely under-declares (a torn head, an
   * estimate from bitrate, mp3 files concatenated end to end), and accusing a
   * healthy 100-minute file because its first Xing frame says 60 s would be
   * the same class of lie this module exists to stop. */
  var LONGER_BAND = 1.25;
  var LONGER_BAND_DECLARED_ONLY = 2;

  function n(v) { return (typeof v === 'number' && isFinite(v) && v > 0) ? v : 0; }

  /**
   * @param {{declaredSec?:number, presentSec?:number, deliveredSec?:number,
   *          deliveredTrusted?:boolean, hasAudio?:boolean, gaps?:number,
   *          deliveredBytes?:number}} f
   * @returns {{state:'full'|'partial'|'unusable', cause:string, recoveredSec:number,
   *            expectedSec:number, missingSec:number, ratio:number, trusted:boolean}}
   */
  function judge(f) {
    f = f || {};
    var declared = n(f.declaredSec), present = n(f.presentSec), delivered = n(f.deliveredSec);
    var out = function (state, cause, expected) {
      var exp = n(expected);
      return {
        state: state, cause: cause,
        recoveredSec: delivered, expectedSec: exp,
        missingSec: exp && delivered ? Math.max(0, exp - delivered) : 0,
        ratio: exp && delivered ? delivered / exp : 0,
        trusted: !!f.deliveredTrusted,
        gaps: f.gaps || 0,
      };
    };

    if (f.hasAudio === false) return out('unusable', 'no_audio', declared || present);
    if (f.deliveredBytes === 0) return out('unusable', 'empty', declared || present);
    if (delivered && delivered < 0.05) return out('unusable', 'empty', declared || present);

    // Longer than the source could possibly be: the output is not this recording.
    var ceiling = Math.max(declared, present);
    var band = present > 0 ? LONGER_BAND : LONGER_BAND_DECLARED_ONLY;
    if (delivered && ceiling && delivered > ceiling * band + 1) {
      return out('unusable', 'longer_than_source', ceiling);
    }

    // Did WE lose audio that the file still held? Only a measured delivered
    // length can accuse the repair — a header-derived one repeats the header.
    if (present && delivered && f.deliveredTrusted !== false
        && delivered < present * FULL_BAND && (present - delivered) >= MIN_MISSING_SEC) {
      return out('partial', 'repair_lost', present);
    }

    // Was the recording already incomplete? present (exact) beats delivered.
    var got = present || delivered;
    if (declared && got && got < declared * FULL_BAND && (declared - got) >= MIN_MISSING_SEC) {
      var r = out('partial', (f.gaps > 0 && got / declared > 0.8) ? 'source_gaps' : 'source_truncated', declared);
      r.recoveredSec = delivered || got;
      return r;
    }

    if (!declared && !present) return out('full', 'unverified', delivered);
    return out('full', 'none', declared || present);
  }

  /**
   * Turn a verdict into the numbers a sentence needs. Formatting and wording
   * belong to the page (10 locales); this only hands over the facts.
   */
  function summary(v) {
    return {
      state: v.state, cause: v.cause,
      recovered: Math.round(v.recoveredSec || 0),
      expected: Math.round(v.expectedSec || 0),
      missing: Math.round(v.missingSec || 0),
      pct: v.ratio ? Math.round(v.ratio * 100) : 0,
    };
  }

  /**
   * compareVideo(before, after) — what a VIDEO repair measurably changed.
   *
   * /repair-video built its result screen from the DIAGNOSIS: every issue found
   * before the repair printed its "fixed" sentence, whatever the repair did.
   * A stream copy of a file whose first frames do not decode therefore said
   * "перекодировали в обход повреждённых кадров", "восстановили SPS/PPS" and
   * "починили заголовок" over output whose frames were byte-for-byte the input's,
   * next to a table that said "problems 4 → 4" and "recovery 60 %" (a number made
   * of the file size and the diagnosis penalty, not of the video) — comment #1784,
   * 2026-09-29. The only honest source of a "fixed" sentence is the same
   * measurement taken on both files.
   *
   * Each side is a snapshot the page builds from the same instruments:
   *   opened      the container opened and listed a video stream
   *   moovMissing the MP4 index was absent
   *   truncated   MEASURED truncation (the box walk ran past the end of the file)
   *   census      VideoVitals.inspect().video — decoded / errors / stalled
   *   play        can THIS browser show a frame: 'ok' | 'error' | 'timeout' | 'na'
   *   framesHinted (before) the probe saw the first frames fail, the census never ran
   * plus, on `after`, durationFixed — the page's observed before→after length fix — and
   * lengthShort {got, of, cause} — `judge()` on the output's length: less video came back than
   * the recording holds ('repair_lost'), or the recording itself is shorter than its header
   * ('source_truncated' / 'source_gaps'). The census cannot see this: a re-encode that gave
   * back 1 MB of an 83 MB recording decodes clean and plays (sessions to 29.09.2026), and the
   * part that is missing is exactly what the person came for — so it is never "fixed".
   * soundLost (after) — the source carried sound the result does not (`remaining: sound`).
   * A missing measurement (null) never produces a sentence either way.
   *
   * @returns {{state:'clean'|'fixed'|'partial'|'unchanged'|'unverified'|'worse',
   *            better:string[], remaining:string[], worse:string[]}}
   */
  function compareVideo(before, after) {
    before = before || {}; after = after || {};
    var better = [], remaining = [], worse = [];
    var bc = before.census || null, ac = after.census || null;
    var bPicDead = !!(bc && bc.decoded === 0 && (bc.stalled || bc.errors > 0));
    var aPicDead = !!(ac && ac.decoded === 0 && (ac.stalled || ac.errors > 0));

    // framesHinted: the probe saw the first frames fail but the census on the source never
    // finished — damage was suspected, not measured, so nothing may be called fixed on it.
    var damagedBefore = !!(before.moovMissing || before.opened === false || before.truncated
      || bPicDead || (bc && bc.errors > 0) || before.play === 'error' || before.durationBad
      || (!bc && before.framesHinted)
      // A length the repair measurably corrected was wrong before it, whether or not the
      // probe could tell (#829: a header saying 0:03 over 40 s is a plausible number).
      || after.durationFixed);

    if ((before.moovMissing || before.opened === false) && after.opened) better.push('container');
    if (before.truncated && after.opened) better.push('truncated');
    if (after.durationFixed) better.push('duration');

    if (bPicDead && ac && ac.decoded > 0) better.push('picture');
    if (aPicDead) remaining.push('picture');
    else if (ac && ac.errors > 0) {
      remaining.push('frames');
      if (bc && bc.errors === 0 && !bPicDead) worse.push('frames');
    } else if (bc && bc.errors > 0 && ac && ac.errors === 0 && !bPicDead) better.push('frames');

    if (before.play === 'error' && after.play === 'ok') better.push('play');
    if (after.play === 'error') {
      remaining.push('play');
      if (before.play === 'ok') worse.push('play');
    }
    if (after.lengthShort) remaining.push('length');
    // The recording had sound and the result has none — an AVI whose header was rebuilt
    // from the frames while its sound chunks named no format (2026-10-10). The picture is
    // back; the sound is a loss the person has to hear about, so it is never "fixed".
    if (after.soundLost) remaining.push('sound');

    var state;
    if (worse.length) state = 'worse';
    else if (!damagedBefore && !remaining.length) state = 'clean';
    else if (better.length && !remaining.length) state = 'fixed';
    else if (better.length) state = 'partial';
    else if (remaining.length) state = 'unchanged';
    else state = 'unverified';   // damage was measured before, the after side was not measured
    return { state: state, better: better, remaining: remaining, worse: worse };
  }

  global.RepairVerdict = { judge: judge, summary: summary, compareVideo: compareVideo, FULL_BAND: FULL_BAND };
})(typeof window !== 'undefined' ? window : globalThis);
